import diffraction_detector_energy from './codev/catalog/diffraction_detector_energy.js';
import diffraction_ensquared_energy from './codev/catalog/diffraction_ensquared_energy.js';
import point_spread from './codev/catalog/point_spread.js';
import yybar from './codev/catalog/yybar.js';
import focal_length_vs_wavelength from './codev/catalog/focal_length_vs_wavelength.js';
import nodal_points from './codev/catalog/nodal_points.js';
import diffraction_line_spread from './codev/catalog/diffraction_line_spread.js';
import geometrical_detector from './codev/catalog/geometrical_detector.js';
import distortion_field from './codev/catalog/distortion_field.js';
import vignetting_factors from './codev/catalog/vignetting_factors.js';
import quadrant_response from './codev/catalog/quadrant_response.js';
import spot_diagram from './codev/catalog/spot_diagram.js';
import zernike_fringe from './codev/catalog/zernike_fringe.js';
import { z } from 'zod';
import { CODEV_MENU } from './codev/codevGroups.js';
import footprint from './codev/catalog/footprint.js';
import { type CodevSettingValue, type CodevSettings, type CodevParameter } from './codev/catalog/shared.js';
import fabrication_data from './codev/catalog/fabrication_data.js';
import distortion_map from './codev/catalog/distortion_map.js';
import astigmatism_field from './codev/catalog/astigmatism_field.js';
import spectral_response from './codev/catalog/spectral_response.js';
import pupil_intensity from './codev/catalog/pupil_intensity.js';
import pupil_map from './codev/catalog/pupil_map.js';
import surface_sag from './codev/catalog/surface_sag.js';
import rms_spot from './codev/catalog/rms_spot.js';
import surface_derivatives from './codev/catalog/surface_derivatives.js';
import diffraction_encircled_energy from './codev/catalog/diffraction_encircled_energy.js';
import surface_curvatures from './codev/catalog/surface_curvatures.js';
import rms_wavefront from './codev/catalog/rms_wavefront.js';
import gaussian_beam from './codev/catalog/gaussian_beam.js';
import wave_aberrations from './codev/catalog/wave_aberrations.js';
import weight from './codev/catalog/weight.js';
import cost_factors from './codev/catalog/cost_factors.js';
import component_focal_lengths from './codev/catalog/component_focal_lengths.js';
import radial_energy from './codev/catalog/radial_energy.js';
import geometrical_line_spread from './codev/catalog/geometrical_line_spread.js';
import transmission from './codev/catalog/transmission.js';
import relative_illumination from './codev/catalog/relative_illumination.js';
import field_curves from './codev/catalog/field_curves.js';
import real_ray_trace from './codev/catalog/real_ray_trace.js';
import paraxial from './codev/catalog/paraxial.js';
import fir_abcd from './codev/catalog/fir_abcd.js';
import paraxial_ray_trace from './codev/catalog/paraxial_ray_trace.js';
import third_order from './codev/catalog/third_order.js';
import mtf from './codev/catalog/mtf.js';
import mtf_field from './codev/catalog/mtf_field.js';
import mtf_map from './codev/catalog/mtf_map.js';
import rms_wavefront_field from './codev/catalog/rms_wavefront_field.js';
import wavefront_polynomial_field from './codev/catalog/wavefront_polynomial_field.js';
import wavefront_polynomial_map from './codev/catalog/wavefront_polynomial_map.js';
import rms_spot_field from './codev/catalog/rms_spot_field.js';
import geometrical_mtf from './codev/catalog/geometrical_mtf.js';
import chromatic_focal_shift from './codev/catalog/chromatic_focal_shift.js';
import lateral_color from './codev/catalog/lateral_color.js';
import ray_fan, { rayFanAgentSettings } from './codev/catalog/ray_fan.js';
export { CodevSettingValueSchema, type CodevSettingValue, type CodevSettings, type CodevRowBlank, type CodevScalarParameter, type CodevParameter, type CodevLensChoice, FmaSliceSettingsSchema, fmaSliceParameters } from './codev/catalog/shared.js';
export { RMS_WAVEFRONT_ANALYSIS } from './codev/catalog/rms_wavefront.js';
export { GAUSSIAN_BEAM_ANALYSIS } from './codev/catalog/gaussian_beam.js';
export { CODEV_MENU, CODEV_MENU_GROUPS, type CodevMenuEntry, type CodevMenuGroup } from './codev/codevGroups.js';
export { CodevMtfFieldOptionsSchema, CODEV_MTF_FIELD_PARAMETERS } from './codev/catalog/mtf_field.js';
export { CodevGeometricalMtfSettingsSchema, CODEV_GEOMETRICAL_MTF_PARAMETERS } from './codev/catalog/geometrical_mtf.js';

export const CODEV_ANALYSIS_CATALOG = {
  diffraction_detector_energy,
  diffraction_ensquared_energy,
  yybar,
  point_spread,
  focal_length_vs_wavelength,
  nodal_points,
  fabrication_data,
  diffraction_line_spread,
  geometrical_detector,
  distortion_field,
  vignetting_factors,
  quadrant_response,
  spot_diagram,
  footprint,
  zernike_fringe,
  distortion_map,
  astigmatism_field,
  spectral_response,
  pupil_intensity,
  pupil_map,
  surface_sag,
  rms_spot,
  surface_derivatives,
  diffraction_encircled_energy,
  surface_curvatures,
  rms_wavefront,
  gaussian_beam,
  wave_aberrations,
  weight,
  cost_factors,
  component_focal_lengths,
  radial_energy,
  geometrical_line_spread,
  transmission,
  relative_illumination,
  field_curves,
  real_ray_trace,
  paraxial,
  fir_abcd,
  paraxial_ray_trace,
  third_order,
  mtf,
  mtf_field,
  mtf_map,
  rms_wavefront_field,
  wavefront_polynomial_field,
  wavefront_polynomial_map,
  rms_spot_field,
  geometrical_mtf,
  chromatic_focal_shift,
  lateral_color,
  ray_fan,
} as const;

export type CodevNativeAnalysis = keyof typeof CODEV_ANALYSIS_CATALOG;
export const CODEV_NATIVE_ANALYSES = Object.keys(CODEV_ANALYSIS_CATALOG) as [CodevNativeAnalysis, ...CodevNativeAnalysis[]];
/** CODE V names of the native reports in display order, for prompt text that names them; derived so it cannot drift from the menu map. */
export const CODEV_NATIVE_REPORT_LABELS = Object.values(CODEV_MENU).map(entry => entry.label).join(', ');
type CatalogSettingsSchema<K extends CodevNativeAnalysis> = (typeof CODEV_ANALYSIS_CATALOG)[K]['settingsSchema'];
type CodevAnalysisSelectionOption = { [K in CodevNativeAnalysis]: z.ZodObject<{
  analysis: z.ZodLiteral<K>; settings: CatalogSettingsSchema<K> | z.ZodPrefault<CatalogSettingsSchema<K>>;
}, z.core.$strict> }[CodevNativeAnalysis];
function selectionSchema(settingsOf: (analysis: CodevNativeAnalysis) => z.ZodType) {
  return z.discriminatedUnion('analysis', CODEV_NATIVE_ANALYSES.map(analysis => {
    const settings = settingsOf(analysis);
    return z.strictObject({ analysis: z.literal(analysis), settings: settings.safeParse({}).success ? settings.prefault({}) : settings });
  }) as unknown as [CodevAnalysisSelectionOption, ...CodevAnalysisSelectionOption[]]);
}
/** One branch per catalog entry. Settings may be omitted only for analyses whose settings all have defaults. */
export const CodevAnalysisSelectionSchema = selectionSchema(analysis => CODEV_ANALYSIS_CATALOG[analysis].settingsSchema);
/** Settings schemas the agent tool uses in place of the catalog's: the same settings, with defaults sized for a tool
 *  result rather than the viewer. */
const AGENT_SETTINGS: { [K in CodevNativeAnalysis]?: CatalogSettingsSchema<K> } = { ray_fan: rayFanAgentSettings };
export const CodevAgentAnalysisSelectionSchema = selectionSchema(analysis => AGENT_SETTINGS[analysis] ?? CODEV_ANALYSIS_CATALOG[analysis].settingsSchema);

/** Extract only defaults declared by the canonical schema; required inputs stay absent.
 * Union branches may repeat a default, but cannot give the same key conflicting defaults. A default one branch
 * declares for a key another branch requires is the "off" value of that other alternative (Spectral Response:
 * RSP's `black_body_kelvin: null` against BLA's `responses: []`); merging every branch's defaults then satisfies no
 * branch while leaving nothing blank for the viewer to ask for (#7447). So a branch contributes its defaults only
 * when every key it requires is required in every branch — the branch the defaults land in — and a key every
 * branch defaults is included regardless. */
export function codevSettingsDefaults(analysis: CodevNativeAnalysis): CodevSettings {
  const schema = z.toJSONSchema(CODEV_ANALYSIS_CATALOG[analysis].settingsSchema);
  const branches = [...(schema.anyOf ?? []), ...(schema.oneOf ?? [])].filter(branch => typeof branch === 'object');
  // Per branch, each property's declared default; undefined where that branch requires the key.
  const shapes = (branches.length > 0 ? branches : [schema]).map(branch => new Map(Object.entries(branch.properties ?? {})
    .map(([key, property]) => [key, typeof property === 'object' && 'default' in property ? { value: property.default as CodevSettingValue } : undefined])));
  const declared = new Map<string, CodevSettingValue>();
  for (const shape of shapes) {
    for (const [key, property] of shape) {
      if (!property) continue;
      if (declared.has(key) && JSON.stringify(declared.get(key)) !== JSON.stringify(property.value)) throw new Error(`Conflicting CODE V default for ${key}`);
      declared.set(key, property.value);
    }
  }
  const defaults: CodevSettings = {};
  for (const shape of shapes) {
    const complete = [...shape].every(([key, property]) => property || !declared.has(key));
    for (const [key, property] of shape) {
      if (property && (complete || shapes.every(other => other.get(key)))) defaults[key] = property.value;
    }
  }
  return defaults;
}

export function codevParameterVisible(parameter: CodevParameter, settings: CodevSettings): boolean {
  return Object.entries(parameter.when ?? {}).every(([key, values]) => values.includes(String(settings[key])));
}

/** Whether the viewer renders the control: visible under `settings` and not `hidden`. A hidden setting keeps its value. */
export function codevParameterShown(parameter: CodevParameter, settings: CodevSettings): boolean {
  return !('hidden' in parameter) && codevParameterVisible(parameter, settings);
}
